import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Chip, EmptyState, Skeleton, Table, TBody, TD, TH, THead, TR } from '../components/ui'
import { PageHeader } from '../components/PageHeader'
import { useProfile, useProfileActivity, useProfileCustomers } from '../api/hooks'
import { API_HOST, ApiError } from '../api/client'
import { SecuritySection } from '../components/features/SecuritySection'
import { EditProfileCard } from '../components/features/EditProfileCard'
import { ChangePasswordCard } from '../components/features/ChangePasswordCard'
import { StepUpProvider } from '../components/features/StepUp'
import { useAuthStore } from '../stores/auth'
import { decodeJwt } from '../lib/jwt'
import { initials, relativeTime } from '../lib/format'

function stampShort(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-4 border-b border-ostrel-line py-2 last:border-0">
      <span className="w-28 shrink-0 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
        {label}
      </span>
      <span className="min-w-0 text-sm text-ostrel-ink">{value}</span>
    </div>
  )
}

export default function ProfilePage() {
  // The provider hosts the step-up modal shared by the sensitive actions
  // below (password change, factor enrollment/removal — tenancy/stepup.py).
  return (
    <StepUpProvider>
      <ProfileContent />
    </StepUpProvider>
  )
}

function ProfileContent() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const logout = useAuthStore((s) => s.logout)
  const selectCustomer = useAuthStore((s) => s.selectCustomer)
  const sessionPrincipal = useAuthStore((s) => s.principal)
  const sessionCustomer = useAuthStore((s) => s.customer)
  const accessToken = useAuthStore((s) => s.accessToken)

  const profile = useProfile()
  const customers = useProfileCustomers()
  const activity = useProfileActivity()

  const [switching, setSwitching] = useState<string | null>(null)
  const [switchError, setSwitchError] = useState<string | null>(null)

  // /profile is still rolling out on some backends — fall back to session data.
  const profileMissing = profile.isError && profile.error instanceof ApiError && profile.error.status === 404
  const principal = profile.data?.principal ?? sessionPrincipal
  const currentCustomer = profile.data?.customer ?? sessionCustomer
  const role = profile.data?.role ?? sessionCustomer?.role ?? ''

  const customerList = customers.data ?? (sessionCustomer ? [sessionCustomer] : [])
  const otherCustomers = customerList.filter((c) => c.unique_id !== currentCustomer?.unique_id)

  const displayName =
    principal && (principal.first_name || principal.last_name)
      ? `${principal.first_name} ${principal.last_name}`.trim()
      : (principal?.username ?? '—')

  const tokenPayload = accessToken ? decodeJwt(accessToken) : null
  const tokenExpiry = tokenPayload?.exp ? new Date(tokenPayload.exp * 1000).toISOString() : null

  const activityItems = activity.data?.pages.flatMap((p) => p.items).slice(0, 8) ?? []
  const activityMissing = activity.isError && activity.error instanceof ApiError && activity.error.status === 404

  const switchTo = async (uid: string) => {
    setSwitching(uid)
    setSwitchError(null)
    try {
      await selectCustomer(uid)
      await queryClient.invalidateQueries()
    } catch (err) {
      setSwitchError(err instanceof ApiError ? err.detail : err instanceof Error ? err.message : String(err))
    } finally {
      setSwitching(null)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        section="Operator profile"
        title="Account & sign-in"
        action={
          <Button
            variant="danger"
            size="md"
            onClick={() => {
              logout()
              navigate('/login')
            }}
          >
            Sign out
          </Button>
        }
      />

      {profile.isPending ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-2">
          <Card title="Identity">
            <div className="flex items-center gap-3.5 border-b border-ostrel-line pb-4">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-sm bg-ostrel-navy text-base font-semibold text-ostrel-gold">
                {initials(displayName !== '—' ? displayName : '?')}
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-2">
                  <span className="truncate font-display text-xl text-ostrel-ink">{displayName}</span>
                  {principal && (
                    <Chip tone="muted" label={principal.kind === 'service_account' ? 'Service account' : 'User'} />
                  )}
                </span>
                {principal?.email && (
                  <span className="tnum mt-0.5 block truncate font-mono text-xs text-ostrel-ink-muted">
                    {principal.email}
                  </span>
                )}
              </span>
            </div>
            <div className="mt-2">
              <DetailRow label="Username" value={<span className="tnum font-mono text-xs">{principal?.username ?? '—'}</span>} />
              <DetailRow label="Role" value={role ? `${role} · this customer` : '—'} />
              <DetailRow label="Customers" value={`${customerList.length} scope${customerList.length === 1 ? '' : 's'}`} />
              <DetailRow
                label="Token scope"
                value={
                  <span className="tnum font-mono text-xs">
                    {currentCustomer ? `customer:${currentCustomer.slug}` : '—'}
                  </span>
                }
              />
              <DetailRow
                label="Last sign-in"
                value={
                  <span className="tnum text-xs text-ostrel-ink-muted">
                    {principal?.last_login ? `${stampShort(principal.last_login)} (${relativeTime(principal.last_login)})` : '—'}
                  </span>
                }
              />
            </div>
            {profileMissing && (
              <p className="mt-3 text-[11px] text-ostrel-ink-muted">
                The profile endpoint is not available yet — showing session data.
              </p>
            )}
            {profile.isError && !profileMissing && (
              <p className="mt-3 text-xs text-ostrel-danger">{profile.error.message}</p>
            )}
          </Card>

          <Card title="Identity & customers">
            {customers.isPending ? (
              <div className="flex flex-col gap-2">
                <Skeleton className="h-12" />
                <Skeleton className="h-12" />
              </div>
            ) : customerList.length === 0 ? (
              <EmptyState title="No customer scopes" hint="Your identity has no customer grants." />
            ) : (
              <ul className="flex flex-col gap-2">
                {currentCustomer && (
                  <li className="flex items-center gap-3 rounded-sm border border-ostrel-gold bg-ostrel-gold/10 px-3 py-2.5">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-ostrel-navy text-[11px] font-semibold text-ostrel-gold">
                      {initials(currentCustomer.name)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-ostrel-ink">{currentCustomer.name}</span>
                      <span className="tnum block truncate font-mono text-[10px] text-ostrel-ink-muted">
                        {currentCustomer.slug}
                      </span>
                    </span>
                    <Chip tone="gold" label={role || 'current'} />
                  </li>
                )}
                {otherCustomers.map((c) => (
                  <li key={c.unique_id} className="flex items-center gap-3 rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2.5">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-ostrel-line bg-ostrel-sand text-[11px] font-semibold text-ostrel-ink-muted">
                      {initials(c.name)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-ostrel-ink">{c.name}</span>
                      <span className="tnum block truncate font-mono text-[10px] text-ostrel-ink-muted">
                        {c.slug}
                      </span>
                    </span>
                    <Chip tone="muted" label={c.role} />
                    <Button variant="ghost" size="sm" disabled={switching !== null} onClick={() => void switchTo(c.unique_id)}>
                      {switching === c.unique_id ? 'Switching…' : 'Switch'}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            {switchError && <p className="mt-3 text-xs text-ostrel-danger">{switchError}</p>}
            <p className="mt-3 text-[11px] leading-relaxed text-ostrel-ink-muted">
              Switching re-scopes the access token to another customer grant; every view reloads in that scope.
            </p>
          </Card>
        </div>
      )}

      {principal?.kind === 'user' && (
        <div className="grid items-start gap-4 lg:grid-cols-2">
          <EditProfileCard />
          <ChangePasswordCard />
        </div>
      )}

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Card title="API endpoint">
          <DetailRow label="Host" value={<span className="tnum font-mono text-xs">{API_HOST}</span>} />
          <DetailRow
            label="Token scope"
            value={
              <span className="tnum font-mono text-xs">
                {currentCustomer ? `customer:${currentCustomer.slug}` : '—'}
              </span>
            }
          />
          <DetailRow
            label="Token expiry"
            value={
              <span className="tnum font-mono text-xs text-ostrel-ink-muted">
                {tokenExpiry ? `${stampShort(tokenExpiry)} (${relativeTime(tokenExpiry)})` : '—'}
              </span>
            }
          />
          <p className="mt-3 text-[11px] leading-relaxed text-ostrel-ink-muted">
            Customer-scoped tokens are short-lived and cannot be refreshed — sign in again when the session
            expires.
          </p>
        </Card>

        <Card
          title="Recent account activity"
          action={
            <Link
              to="/audit"
              className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold hover:text-ostrel-accent"
            >
              Audit trail →
            </Link>
          }
        >
          {activity.isPending ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-8" />
              <Skeleton className="h-8" />
              <Skeleton className="h-8" />
            </div>
          ) : activityMissing ? (
            <EmptyState title="Activity endpoint not available yet" hint="Account activity lands here once the backend ships it." />
          ) : activity.isError ? (
            <EmptyState title="Could not load activity" hint={activity.error.message} />
          ) : activityItems.length === 0 ? (
            <EmptyState title="No recent activity" hint="Sign-ins, unlocks and changes made by this account appear here." />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Timestamp</TH>
                  <TH>Action</TH>
                  <TH>Target</TH>
                </TR>
              </THead>
              <TBody>
                {activityItems.map((entry) => (
                  <TR key={entry.unique_id}>
                    <TD>
                      <span className="tnum font-mono text-xs text-ostrel-ink-muted">{stampShort(entry.at)}</span>
                    </TD>
                    <TD>
                      <span className="tnum font-mono text-xs text-ostrel-ink">{entry.action}</span>
                    </TD>
                    <TD>
                      <span className="tnum font-mono text-xs text-ostrel-ink-muted">
                        {entry.target_unique_id ?? '—'}
                      </span>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </Card>
      </div>

      <SecuritySection />
    </div>
  )
}
